import { GoogleOAuthProvider } from "@react-oauth/google";

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import jwtDecode from "jwt-decode";
import NotistackProvider from "./components/NotistackProvider";
import { ProgressBarStyle } from "./components/ProgressBar";
import RtlLayout from "./components/RtlLayout";
import ScrollToTop from "./components/ScrollToTop";
import ThemeColorPresets from "./components/ThemeColorPresets";
import MotionLazyContainer from "./components/animate/MotionLazyContainer";
import { ChartStyle } from "./components/chart";
import FloatingAction from "./components/floating-action";
import Settings from "./components/settings";
import Ternary from "./components/ternary";
import Visitor from "./components/visitor";
import { ENABLE_VISITOR_MODE, GOOGLE_CLIENT_ID, NMI_QUERY_URL, NMI_KEY } from "./config";
import useAuth from "./hooks/useAuth";
import useGetCurrency from "./layouts/shared/header/components/currency-popover/hooks/use-get-currency";
import Router from "./routes";
import { useAppConfig } from "./store/app-config";
import ThemeProvider from "./theme";
import axiosInstance from "./utils/axios";
import { getSession } from "./utils/jwt";
// import LoginPopup from './components/LoginPopup';
import LoginSubscriptionPopup from './components/LoginSubscriptionPopup';
import NmiBillingPopup from './components/NmiBillingPopup';

const getChargedThroughNmi = (data) => {
  if (!data || typeof data !== "object") return false;
  const keys = Object.keys(data);
  const normalizedKey = keys.find(
    (key) => key.replace(/\s+/g, "_").toLowerCase() === "charged_through_nmi"
  );
  if (normalizedKey) {
    return Boolean(data[normalizedKey]);
  }
  if (Object.prototype.hasOwnProperty.call(data, "charged through NMI")) {
    return Boolean(data["charged through NMI"]);
  }
  if (Object.prototype.hasOwnProperty.call(data, "charged_through_nmi")) {
    return Boolean(data.charged_through_nmi);
  }
  if (Object.prototype.hasOwnProperty.call(data, "chargedThroughNMI")) {
    return Boolean(data.chargedThroughNMI);
  }
  return false;
};

const getDistributorIdForNmi = (user) => {
  const storedUserId = localStorage.getItem("user_id");
  if (storedUserId) {
    const parsed = Number(storedUserId);
    return Number.isFinite(parsed) ? parsed : null;
  }

  let tokenId = null;
  const token = localStorage.getItem("accessToken");
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tokenId =
        decoded?.user_profile?.user_id ??
        decoded?.user_id ??
        decoded?.distributor_id ??
        decoded?.distributorId ??
        decoded?.id ??
        null;
    } catch (err) {
      tokenId = null;
    }
  }

  const fallback =
    user?.user_profile?.user_id ?? user?.id ?? tokenId ?? null;
  if (fallback == null) return null;
  const parsed = Number(fallback);
  return Number.isFinite(parsed) ? parsed : null;
};

const fetchBrandSettings = async () => {
  try {
    const { data, status } = await axiosInstance("api/company-logos");
    if (status === 200) {
      const { logo, side_bar_logo, favicon } = data.data;
      if (logo) {
        localStorage.setItem("logo", logo);
        localStorage.setItem("side_bar_logo", logo);
        localStorage.setItem("favicon", favicon);
      }
    }
  } catch (err) {
    console.error(err);
  }
};

export default function App() {
  const fetchCurrency = useGetCurrency();
  const { config, setConfig } = useAppConfig();
  const { getUser, user, isAdmin, isSubAdmin } = useAuth();
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [nmiPopupEnabled, setNmiPopupEnabled] = useState(false);
  const nmiCheckRef = useRef(false);
  const nmiCancelledRef = useRef(false);
  const isImpersonate = localStorage.getItem("isImpersonate") === "true";

  useEffect(() => {
    if (user && !Object.keys(user).length) {
      getUser();
      fetchCurrency(isAdmin || isSubAdmin);
      return;
    }
  }, [user]);

  useEffect(() => {
    const fetchAppConfig = async () => {
      try {
        const { data } = await axiosInstance("api/config-settings");
        const test = data.data.reduce((acc, curr) => {
          const { code, status, value } = curr || {};

          return {
            ...acc,
            [code]: { status: Boolean(status), value },
          };
        }, {});
        setConfig(test);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAppConfig();
  }, []);

  useEffect(() => {
    fetchBrandSettings();
  }, []);

  useEffect(() => {
    return () => {
      nmiCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    console.log("[NMI] popup enabled state", nmiPopupEnabled);
  }, [nmiPopupEnabled]);

  useEffect(() => {
    const isLoggedIn = getSession();
    if (!isLoggedIn) return;
    if (nmiCheckRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const isAdminFlag = localStorage.getItem("isAdmin") === "true";
    const isSubAdminFlag = localStorage.getItem("isSubAdmin") === "true";
    const isImpersonateFlag = localStorage.getItem("isImpersonate") === "true";
    if (isAdminFlag || isSubAdminFlag || isImpersonateFlag) return;
    console.log("[NMI] check start", {
      isLoggedIn,
      isAdminFlag,
      isSubAdminFlag,
      userLoaded: Boolean(user && Object.keys(user).length),
    });

    if (params.get("showPopup") === "true") {
      params.delete("showPopup");
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
      }`;
      window.history.replaceState({}, "", nextUrl);
    }

    const distributorId = getDistributorIdForNmi(user);
    console.log("[NMI] distributorId", distributorId);
    if (!distributorId) return;

    nmiCheckRef.current = true;

    const run = async () => {
      try {
        const { data } = await axios.post(
          NMI_QUERY_URL,
          {
            distributor_id: distributorId,
            key: NMI_KEY,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const chargedThroughNmi = getChargedThroughNmi(data?.response ?? data);
        console.log("[NMI] query response", data);
        console.log("[NMI] chargedThroughNmi", chargedThroughNmi);

        if (!chargedThroughNmi) {
          console.log("[NMI] redirecting to /user/landing");
          if (!nmiCancelledRef.current) {
            if (window.location.pathname !== "/user/landing") {
              window.location = "/user/landing";
            }
          }
          return;
        }

        if (!nmiCancelledRef.current) {
          console.log("[NMI] enabling billing popup");
          setNmiPopupEnabled(true);
        }
      } catch (err) {
        console.error("Failed to load NMI query:", err);
        if (!nmiCancelledRef.current) {
          if (window.location.pathname !== "/user/landing") {
            window.location = "/user/landing";
          }
        }
      }
    };

    run();
  }, [user]);

// useEffect(() => {
//   const isLoggedIn = getSession();
  
//   if (isLoggedIn && user && !isImpersonate) {
//     // Directly check preauth status from your API
//     // You might want to add this check in your LoginSubscriptionPopup component
//     // or here in App.js before showing the popup
//     const checkPreauth = async () => {
//       try {
//         const response = await axiosInstance.get('/api/user/dashboard/preauth-status');
//         if (response.data.preauth_status === 'pending') {
//           // Show popup only if status is pending
//           setShowSubscriptionPopup(true);
//         }
//       } catch (error) {
//         console.error('Error checking preauth status:', error);
//       }
//     };
    
//     checkPreauth();
//   }
// }, [user]);

// const handleSubscriptionSuccess = (option, response) => {
//   console.log('Subscription response:', option, response);
//   // Handle success - update user state or call API if needed
//   setShowSubscriptionPopup(false);
  
//   // You might want to update the user's subscription status in your backend
//   // or refresh user data here
//   if (user && !Object.keys(user).length) {
//     getUser(); // Refresh user data
//   }
// };

// const handleSubscriptionCancel = (option, response) => {
//   console.log('Subscription cancelled:', option, response);
//   setShowSubscriptionPopup(false);
  
//   // Refresh user data if needed
//   if (user && !Object.keys(user).length) {
//     getUser();
//   }
// };

  const isLoggedIn = getSession();
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <ThemeColorPresets>
          <RtlLayout>
            <NotistackProvider>
              <MotionLazyContainer>
                <ProgressBarStyle />
                <ChartStyle />
                <Settings />
                <ScrollToTop />
                <Router />
                <NmiBillingPopup
                  enabled={nmiPopupEnabled}
                  onClose={() => setNmiPopupEnabled(false)}
                />
                {/* <FloatingAction /> */}

                <Ternary
                  when={ENABLE_VISITOR_MODE && isLoggedIn}
                  then={<Visitor />}
                />
                {/* <LoginPopup/> */}
                 {/* <LoginSubscriptionPopup
                  showOnLogin={showSubscriptionPopup && !isImpersonate}
                  onSuccess={handleSubscriptionSuccess}
                  onCancel={handleSubscriptionCancel}
                  userName={user?.user_profile || user?.email || "Subscriber"}
                  user={user} // Pass user object for subscription checks
                /> */}
              </MotionLazyContainer>
            </NotistackProvider>
          </RtlLayout>
        </ThemeColorPresets>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

